import { NavyAtmosphere, ScrollReveal, SplitReveal } from '@/components/motion';
import { EDUQUA_FACTS, EDUQUA_STATEMENT } from '@/data/about';

/**
 * EduQuaCertification (/about — section 5) — "EduQua certification". A
 * text-only navy statement section: the accreditation-mandated wording
 * "IBAS is EduQua Certified." is featured as a large SplitReveal display
 * headline over NavyAtmosphere, framed by a short lede explaining EduQua as
 * the Swiss quality certification IBAS holds. EDUQUA_FACTS render below as
 * glass-on-navy cards, each with its Icon in a brand-gold-light chip.
 *
 * Compliance: per Appendix F, this section carries NO logo or mark of any
 * kind — the EduQua mark is reserved for the dedicated EduQua page only, and
 * is never shown beside the other accreditation marks. Every fact is read
 * verbatim from EDUQUA_STATEMENT / EDUQUA_FACTS in '@/data/about'; nothing
 * is invented or reworded.
 *
 * Motion: SplitReveal headline, ScrollReveal lede + child-stagger fact grid.
 * All primitives are reduced-motion safe and never strand content hidden.
 */
export function EduQuaCertification() {
  return (
    <section className="relative isolate overflow-hidden bg-brand-navy py-16 md:py-24">
      <NavyAtmosphere />

      <div className="relative z-10 mx-auto w-full max-w-6xl px-6">
        <div className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold-light uppercase">
            Certification
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-white md:text-5xl"
          >
            {EDUQUA_STATEMENT}
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-white/70 md:text-lg">
              EduQua is the Swiss quality certification IBAS holds — a national standard for
              adult and continuing education, set out below.
            </p>
          </ScrollReveal>
        </div>

        {/* ── EduQua facts — glass-on-navy cards ────────────────────────── */}
        <ScrollReveal
          stagger={0.1}
          className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
        >
          {EDUQUA_FACTS.map((fact) => (
            <div
              key={fact.label}
              className="h-full rounded-2xl border border-white/10 bg-white/5 p-6 transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/40 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <div className="flex size-11 items-center justify-center rounded-xl bg-brand-gold-light/10 text-brand-gold-light">
                <fact.Icon aria-hidden className="size-5" />
              </div>
              <p className="mt-5 text-sm leading-snug font-medium text-white">{fact.label}</p>
              <p className="mt-2 text-xs leading-relaxed text-white/60">{fact.value}</p>
            </div>
          ))}
        </ScrollReveal>
      </div>
    </section>
  );
}
